import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Button from '../components/common/Button';
import LoadingSpinner from '../components/common/LoadingSpinner';
import CalendrierPlagesHoraires from '../components/beneficiaires/CalendrierPlagesHoraires';
import { useToast } from '../components/common/Toast';
import { ArrowLeft, MapPin, Phone, AlertCircle, Save, Edit, X, User } from 'lucide-react';
import api from '../services/api';

const BeneficiaireDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isCoordinatrice } = useAuth();
  const [beneficiaire, setBeneficiaire] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState(null);
  const { showToast, ToastContainer } = useToast();

  useEffect(() => {
    loadBeneficiaire();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadBeneficiaire = async () => {
    try {
      const response = await api.get(`/beneficiaires/${id}`);
      setBeneficiaire(response.data);
      setFormData(response.data);
    } catch (error) {
      showToast('Erreur chargement bénéficiaire', 'error');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await api.put(`/beneficiaires/${id}`, formData);
      showToast('Bénéficiaire mis à jour', 'success');
      setEditing(false);
      loadBeneficiaire();
    } catch (error) {
      showToast(error.response?.data?.message || 'Erreur mise à jour', 'error');
    }
  };

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  const vigilanceColors = {
    vert: { bg: 'bg-emerald-500', badge: 'bg-emerald-50 text-emerald-700 border-emerald-300', border: 'border-emerald-300' },
    orange: { bg: 'bg-orange-500', badge: 'bg-orange-50 text-orange-700 border-orange-300', border: 'border-orange-300' },
    rouge: { bg: 'bg-red-500', badge: 'bg-red-50 text-red-700 border-red-300', border: 'border-red-300' }
  };

  const vigilanceColor = vigilanceColors[beneficiaire.niveauVigilance];

  return (
    <div className="pb-20 bg-gradient-to-br from-blue-50 via-white to-purple-50 min-h-screen">
      <ToastContainer />
      
      {/* Header moderne */}
      <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-purple-700 text-white p-6 rounded-b-[2rem] shadow-xl sticky top-0 z-10">
        <button 
          onClick={() => navigate(-1)} 
          className="flex items-center gap-2 mb-3 bg-white/20 hover:bg-white/30 backdrop-blur-sm px-3 py-2 rounded-xl transition-all active:scale-95"
        >
          <ArrowLeft size={20} />
          <span className="text-sm font-medium">Retour</span>
        </button>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 backdrop-blur-sm rounded-full p-3">
              <User size={28} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold drop-shadow-sm">
                {beneficiaire.prenom} {beneficiaire.nom}
              </h1>
              <p className="text-blue-100 text-sm font-medium">Fiche bénéficiaire</p>
            </div>
          </div>
          <div className={`px-4 py-2 rounded-xl ${vigilanceColor.badge} border-2 font-bold text-sm shadow-lg`}>
            {beneficiaire.niveauVigilance.toUpperCase()}
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Informations générales */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl p-2">
                <MapPin size={20} className="text-white" />
              </div>
              <h2 className="text-lg font-bold text-gray-800">Informations</h2>
            </div>
            {isCoordinatrice && !editing && (
              <button
                onClick={() => setEditing(true)}
                className="flex items-center gap-2 bg-blue-50 hover:bg-blue-100 text-blue-700 font-bold px-4 py-2 rounded-xl border-2 border-blue-200 transition-all active:scale-95"
              >
                <Edit size={18} />
                Modifier
              </button>
            )}
          </div>

          <div className="space-y-3">
            <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-4 flex items-start gap-3 border border-gray-200">
              <MapPin size={20} className="text-blue-500 mt-0.5 flex-shrink-0" />
              <div className="text-gray-700 font-medium">
                <p>{beneficiaire.adresse.rue}</p>
                <p className="text-sm">{beneficiaire.adresse.codePostal} {beneficiaire.adresse.ville}</p>
              </div>
            </div>

            {beneficiaire.telephone && (
              <div className="bg-gradient-to-r from-gray-50 to-green-50 rounded-xl p-4 flex items-center gap-3 border border-gray-200">
                <div className="bg-green-500 rounded-full p-2">
                  <Phone size={18} className="text-white" />
                </div>
                <a 
                  href={`tel:${beneficiaire.telephone}`}
                  className="text-gray-700 font-bold hover:text-green-600 transition-colors"
                >
                  {beneficiaire.telephone}
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Consignes */}
        {beneficiaire.consignes && (
          <div className={`bg-gradient-to-r from-amber-50 to-yellow-50 border-2 ${vigilanceColor.border} rounded-2xl shadow-lg p-5`}>
            <div className="flex gap-3">
              <div className={`${vigilanceColor.bg} rounded-full p-2 h-fit`}>
                <AlertCircle size={24} className="text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                  ⚠️ Consignes importantes
                </h3>
                <p className="text-sm text-gray-800 leading-relaxed font-medium">
                  {beneficiaire.consignes}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Contact urgence */}
        {beneficiaire.contactUrgence?.telephone && (
          <div className="bg-gradient-to-r from-red-50 to-pink-50 border-2 border-red-300 rounded-2xl shadow-lg p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="bg-red-500 rounded-full p-2">
                <Phone size={20} className="text-white" />
              </div>
              <h3 className="font-bold text-red-800 text-lg">🚨 Contact d'urgence</h3>
            </div>
            <div className="bg-white rounded-xl p-3 mb-3">
              <p className="text-sm text-gray-800 font-bold">
                {beneficiaire.contactUrgence.nom}
                {beneficiaire.contactUrgence.lien && (
                  <span className="text-gray-600 font-normal"> ({beneficiaire.contactUrgence.lien})</span>
                )}
              </p>
              <a 
                href={`tel:${beneficiaire.contactUrgence.telephone}`}
                className="text-sm font-bold text-red-600 hover:text-red-700 mt-1 block"
              >
                📞 {beneficiaire.contactUrgence.telephone}
              </a>
            </div>
          </div>
        )}

        {/* Calendrier des plages horaires */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl p-2">
              <AlertCircle size={20} className="text-white" />
            </div>
            <h2 className="text-lg font-bold text-gray-800">📅 Plages horaires d'intervention</h2>
          </div>
          <CalendrierPlagesHoraires 
            plages={editing ? formData.plagesHoraires : beneficiaire.plagesHoraires}
            onChange={(nouvelles) => setFormData({...formData, plagesHoraires: nouvelles})}
            readOnly={!editing}
          />
        </div>

        {/* Boutons d'action en mode édition */}
        {editing && (
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={handleSave}
              className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-2 shadow-lg transition-all active:scale-95"
            >
              <Save size={22} />
              Enregistrer
            </button>
            <button
              onClick={() => {
                setEditing(false);
                setFormData(beneficiaire);
              }}
              className="bg-white hover:bg-gray-50 text-gray-700 font-bold py-4 rounded-xl flex items-center justify-center gap-2 border-2 border-gray-300 transition-all active:scale-95"
            >
              <X size={22} />
              Annuler
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BeneficiaireDetailPage;
